import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  ArrowLeft, RefreshCw, ChevronDown, ChevronUp, Calendar,
  Package, Users, CreditCard, ExternalLink
} from 'lucide-react';
import { assetService } from '../services/assetService';
import { userService } from '../services/userService';
import api from '../utils/api';
import adminApi from '../utils/adminApi';

const AssetStatusPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [individualAssets, setIndividualAssets] = useState([]);
  const [curations, setCurations] = useState([]);
  const [expandedAssets, setExpandedAssets] = useState({});
  const [bookingsData, setBookingsData] = useState({});

  // Progress status options
  const progressStatuses = [
    'Expression of Interest',
    'Ticket Blocking Payment',
    'Due Diligence & Paperwork',
    'Group Curated',
    'SPV Formed',
    'Stage 1 Paid',
    'Asset Purchased',
    'Asset Deployed'
  ];

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      // Try admin backend first, fallback to main website backend
      let response;
      try {
        // Fetch aggregated asset bookings from admin backend
        response = await adminApi.get('/api/admin/asset-bookings');
      } catch (adminError) {
        console.warn('Admin backend asset-bookings not available, trying main website backend...');
        // Fallback to main website backend
        response = await api.get('/api/admin/asset-bookings');
      }
      
      const data = response.data;
      
      setIndividualAssets(data.individual_assets || []);
      setCurations(data.curation_packages || []);
      
      // Build bookings data map for easy access
      const bookingsMap = {};
      data.individual_assets?.forEach(asset => {
        bookingsMap[asset.asset_id] = asset.bookings || [];
      });
      data.curation_packages?.forEach(pkg => {
        bookingsMap[pkg.package_id] = pkg.bookings || [];
      });
      setBookingsData(bookingsMap);
      
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleExpand = (assetId) => {
    setExpandedAssets(prev => ({
      ...prev,
      [assetId]: !prev[assetId]
    }));
  };

  const calculateDaysLapsed = (listingDate) => {
    if (!listingDate) return 'N/A';
    const listed = new Date(listingDate);
    const now = new Date();
    const diffTime = Math.abs(now - listed);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getBookingsSummary = (assetId) => {
    const bookings = bookingsData[assetId] || [];
    const totalEOI = bookings.length;
    const totalTicketsBooked = bookings.reduce((sum, b) => sum + (b.tickets || 0), 0);
    const totalAmount = bookings.reduce((sum, b) => sum + (b.total_paid || 0), 0);
    
    return { totalEOI, totalTicketsBooked, totalAmount, allBookings: bookings };
  };

  const updateBookingProgress = async (userAssetId, updateData) => {
    try {
      const response = await adminApi.put(`/api/admin/user-assets/${userAssetId}`, updateData);
      const result = response.data;
      
      if (result.success) {
        // Show success message (you can add a toast notification here)
        console.log('Booking updated successfully');
        alert('Booking updated successfully!');
        // Refresh data
        fetchAllData();
      } else {
        console.error('Update failed:', result.message);
        alert(`Update failed: ${result.message}`);
      }
    } catch (error) {
      console.error('Error updating booking:', error);
      alert('Failed to update booking. Please try again.');
    }
  };

  const handleCancellationDecision = async (userAssetId, decision) => {
    if (!decision) return;
    
    // Show confirmation dialog
    const confirmMessage = decision === 'accept' 
      ? 'Are you sure you want to accept this cancellation? The asset will be removed from user\'s dashboard.'
      : 'Are you sure you want to reject this cancellation? The booking will be restored to active state.';
    
    const confirmed = window.confirm(confirmMessage);
    
    if (!confirmed) return;
    
    try {
      // API expects decision as query parameter
      const response = await adminApi.put(
        `/api/user-assets/${userAssetId}/cancellation-decision?decision=${decision}`
      );
      
      const data = response.data;
      alert(data.message || 'Cancellation decision processed successfully');
      
      // Refresh asset bookings data
      await fetchAllData();
    } catch (error) {
      console.error('Error processing cancellation decision:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to process cancellation decision';
      alert(`Failed to process cancellation decision: ${errorMessage}`);
    }
  };

  const AssetCard = ({ asset, isPackage = false }) => {
    const assetId = isPackage ? asset.package_id : asset.asset_id;
    const assetName = isPackage ? asset.package_name : asset.asset_name;
    const isExpanded = expandedAssets[assetId];
    const summary = getBookingsSummary(assetId);
    const totalTickets = asset.total_tickets || 12;
    const fulfilledTickets = asset.tickets_booked || 0;
    const daysLapsed = asset.days_lapsed || 0;

    return (
      <Card className="mb-4">
        {/* Minimalist Horizontal Tab */}
        <div className="p-4 hover:bg-gray-50 transition-colors">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4 flex-1">
              {/* Asset ID */}
              <div className="min-w-[120px]">
                <span className="text-xs text-gray-500">Asset ID</span>
                <p className="font-mono text-sm font-medium">
                  {asset.asset_identification_number || asset.id?.substring(0, 8) || 'N/A'}
                </p>
              </div>

              {/* Asset Name */}
              <div className="min-w-[200px]">
                <span className="text-xs text-gray-500">Asset Name</span>
                <p className="font-medium text-sm">{assetName}</p>
              </div>

              {/* Date of Listing */}
              <div className="min-w-[120px]">
                <span className="text-xs text-gray-500">Listed On</span>
                <p className="text-sm flex items-center gap-1">
                  <Calendar size={14} />
                  {asset.listed_on ? new Date(asset.listed_on).toLocaleDateString('en-GB') : 'N/A'}
                </p>
              </div>

              {/* Days Lapsed */}
              <div className="min-w-[100px]">
                <span className="text-xs text-gray-500">Days Lapsed</span>
                <p className="text-sm font-medium">{daysLapsed} days</p>
              </div>

              {/* Fulfilled Tickets */}
              <div className="min-w-[120px]">
                <span className="text-xs text-gray-500">Tickets</span>
                <p className="text-sm font-semibold">
                  <span className="text-green-600">{fulfilledTickets}</span>
                  <span className="text-gray-400">/{totalTickets}</span>
                </p>
              </div>
            </div>

            {/* Expand View Button */}
            <Button
              variant="outline"
              size="sm"
              onClick={() => toggleExpand(assetId)}
              className="flex items-center gap-2"
            >
              {isExpanded ? 'Collapse' : 'Expand View'}
              {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </Button>
          </div>
        </div>

        {/* Expanded View */}
        {isExpanded && (
          <div className="border-t">
            {/* Summary Section */}
            <div className="p-6 bg-blue-50">
              <h3 className="text-lg font-semibold mb-4">Summary</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Users size={16} />
                      <span className="text-sm">Total EOI</span>
                    </div>
                    <p className="text-2xl font-bold text-blue-600">{summary.totalEOI}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <Package size={16} />
                      <span className="text-sm">Tickets Booked (Paid)</span>
                    </div>
                    <p className="text-2xl font-bold text-green-600">{summary.totalTicketsBooked}</p>
                  </CardContent>
                </Card>
                
                <Card>
                  <CardContent className="pt-4">
                    <div className="flex items-center gap-2 text-gray-600 mb-1">
                      <CreditCard size={16} />
                      <span className="text-sm">Total Amount Collected</span>
                    </div>
                    <p className="text-2xl font-bold text-purple-600">
                      ₹{summary.totalAmount.toFixed(2)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Total paid by {summary.totalEOI} member{summary.totalEOI !== 1 ? 's' : ''}
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>

            {/* Separator Line */}
            <div className="border-t border-gray-300"></div>

            {/* Bookings List Section */}
            <div className="p-6">
              <h3 className="text-lg font-semibold mb-4">Bookings List</h3>
              
              {summary.allBookings.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users size={48} className="mx-auto mb-2 opacity-30" />
                  <p>No bookings yet for this asset</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {summary.allBookings.map((booking, index) => {
                    // Track original and edited values
                    const [editedStatus, setEditedStatus] = useState(booking.status);
                    const [editedStage, setEditedStage] = useState(booking.stage);
                    const [editedTotalPaid, setEditedTotalPaid] = useState(booking.total_paid);
                    
                    const handleUpdate = () => {
                      // Build update payload with ONLY changed fields
                      const updatePayload = {};
                      
                      // Only add status if it was changed
                      if (editedStatus !== booking.status) {
                        updatePayload.status = editedStatus;
                      }
                      
                      // Only add stage if it was changed
                      if (editedStage !== booking.stage) {
                        updatePayload.stage = editedStage;
                      }
                      
                      // Only add total_paid if it was changed
                      if (editedTotalPaid !== booking.total_paid) {
                        updatePayload.total_paid = editedTotalPaid;
                      }
                      
                      // Check if any field was actually changed
                      if (Object.keys(updatePayload).length === 0) {
                        alert('No changes to update. Please modify at least one field.');
                        return;
                      }
                      
                      // Log what's being sent for debugging
                      console.log('Updating user asset:', booking.user_asset_id, 'with payload:', updatePayload);
                      
                      // Send update with only changed fields
                      updateBookingProgress(booking.user_asset_id, updatePayload);
                    };
                    
                    return (
                      <Card key={booking.user_asset_id || index} className="p-4">
                        <div className="space-y-4">
                          {/* Row 1: Basic Info */}
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">Member Name</Label>
                              <p className="font-medium">{booking.user_name || 'N/A'}</p>
                            </div>
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">City</Label>
                              <p className="text-sm">{booking.user_city || 'N/A'}</p>
                            </div>
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">Tickets Booked</Label>
                              <p className="text-sm font-semibold">{booking.tickets || 0}</p>
                            </div>
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">Booking Date</Label>
                              <p className="text-sm">{booking.booking_date ? new Date(booking.booking_date).toLocaleDateString('en-GB') : 'N/A'}</p>
                            </div>
                            <div>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => window.open(`/users/${booking.user_id}`, '_blank')}
                                className="flex items-center gap-2"
                              >
                                <ExternalLink size={14} />
                                View Member
                              </Button>
                            </div>
                          </div>

                          {/* Row 2: Status Updates */}
                          <div className="flex items-end gap-4 pt-3 border-t">
                            {/* Status Dropdown */}
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">Status</Label>
                              <Select value={editedStatus} onValueChange={setEditedStatus}>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select status" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="expression_of_interest">Expression of Interest</SelectItem>
                                  <SelectItem value="payment_review">Payment Under Review</SelectItem>
                                  <SelectItem value="stage_1_confirmed">Stage 1 Confirmed</SelectItem>
                                  <SelectItem value="stage_2_payment">Ready for Stage 2 Payment</SelectItem>
                                  <SelectItem value="stage_2_paid">Stage 2 Payment Complete</SelectItem>
                                  <SelectItem value="deployed">Asset Deployed</SelectItem>
                                  <SelectItem value="active">Active Ownership</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Stage Dropdown */}
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">Process Progress</Label>
                              <Select value={editedStage} onValueChange={setEditedStage}>
                                <SelectTrigger className="w-full">
                                  <SelectValue placeholder="Select stage" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="stage_1_booking">Expression of Interest / Ticket Blocking</SelectItem>
                                  <SelectItem value="stage_2_due_diligence">Due Diligence & Paperwork</SelectItem>
                                  <SelectItem value="stage_3_group_curated">Group Curated</SelectItem>
                                  <SelectItem value="stage_4_spv_formed">SPV Formed</SelectItem>
                                  <SelectItem value="stage_5_stage_1_paid">Stage 1 Paid</SelectItem>
                                  <SelectItem value="stage_6_asset_purchased">Asset Purchased</SelectItem>
                                  <SelectItem value="stage_7_asset_deployed">Asset Deployed</SelectItem>
                                </SelectContent>
                              </Select>
                            </div>

                            {/* Total Paid */}
                            <div className="flex-1">
                              <Label className="text-xs text-gray-500">Total Paid (₹)</Label>
                              <Input
                                type="number"
                                placeholder="0"
                                value={editedTotalPaid || ''}
                                onChange={(e) => setEditedTotalPaid(parseFloat(e.target.value) || 0)}
                                className="w-full"
                              />
                            </div>

                            {/* Update Button */}
                            <div>
                              <Button
                                onClick={handleUpdate}
                                className="bg-green-600 hover:bg-green-700 text-white"
                                size="sm"
                              >
                                Update
                              </Button>
                            </div>
                          </div>

                          {/* Cancellation Management Section */}
                          {booking.cancellation_requested && booking.cancellation_status === 'pending' && (
                            <div className="mt-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                              <div className="flex justify-between items-start">
                                <div className="flex-1">
                                  <p className="text-red-600 font-semibold text-sm mb-1">
                                    Cancellation Requested
                                  </p>
                                  <p className="text-gray-500 text-sm">
                                    Reason: {booking.cancellation_reason || 'No reason provided'}
                                  </p>
                                </div>
                                
                                <Select 
                                  onValueChange={(value) => handleCancellationDecision(booking.user_asset_id, value)}
                                  defaultValue=""
                                >
                                  <SelectTrigger className="ml-4 w-[180px]">
                                    <SelectValue placeholder="Select Action" />
                                  </SelectTrigger>
                                  <SelectContent>
                                    <SelectItem value="accept">Accept</SelectItem>
                                    <SelectItem value="reject">Reject</SelectItem>
                                  </SelectContent>
                                </Select>
                              </div>
                            </div>
                          )}
                        </div>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>
          </div>
        )}
      </Card>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900 mb-4"></div>
          <p className="text-gray-600">Loading asset status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="outline"
                onClick={() => navigate('/dashboard')}
                className="flex items-center gap-2"
                size="sm"
              >
                <ArrowLeft size={16} />
                <span className="hidden sm:inline">Back</span>
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Asset Status</h1>
                <p className="text-xs sm:text-sm text-gray-600 mt-1">
                  Track and manage asset bookings and progress
                </p>
              </div>
            </div>

            <Button
              variant="outline"
              size="sm"
              onClick={fetchAllData}
              className="flex items-center gap-2"
              disabled={loading}
            >
              <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Individual Cars Section */}
        <section className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Package className="text-blue-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">Individual Cars</h2>
            <span className="text-sm text-gray-500">({individualAssets.length} assets)</span>
          </div>
          
          {individualAssets.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                No individual assets found
              </CardContent>
            </Card>
          ) : (
            individualAssets.map((asset) => (
              <AssetCard key={asset.id} asset={asset} isPackage={false} />
            ))
          )}
        </section>

        {/* 360 Day Curations Section */}
        <section>
          <div className="flex items-center gap-2 mb-4">
            <Package className="text-purple-600" size={24} />
            <h2 className="text-xl font-bold text-gray-900">360 Day Curations</h2>
            <span className="text-sm text-gray-500">({curations.length} packages)</span>
          </div>
          
          {curations.length === 0 ? (
            <Card>
              <CardContent className="py-8 text-center text-gray-500">
                No curation packages found
              </CardContent>
            </Card>
          ) : (
            curations.map((curation) => (
              <AssetCard key={curation.id} asset={curation} isPackage={true} />
            ))
          )}
        </section>
      </main>
    </div>
  );
};

export default AssetStatusPage;
